import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { IUser } from '@shared/models/iuser.model';
import { LanguageLocale } from '@shared/models/language.model';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { AppStorageService, LOCALE_ID, USER_INFO_TOKEN } from '@shared/services/app-storage.service';
import { UserService } from '@user/services/user.service';
import { FEATURE_TOGGLER, FeatureToggler } from '@cbre/feature-toggles';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { AdminService } from '@admin/services/admin.service';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';

@UntilDestroy()
@Injectable()
export class ActiveGuardService {
  constructor(
    private appStorageService: AppStorageService,
    private router: Router,
    private userService: UserService,
    private adminService: AdminService,
    private userInfoResolver: UserInfoResolver,
    @Inject(FEATURE_TOGGLER) private featureToggler: FeatureToggler,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {}

  canActivate(): Observable<boolean> | Promise<boolean> | boolean {
    // Getting the info set by @cbre/sso
    const localUser = this.appStorageService.getItem<IUser>(USER_INFO_TOKEN);
    if (!localUser.id) {
      this.router.navigate(['access-request']);
      return false;
    }

    //Set user Locale ID to local storage
    const userLocale$ = this.userService.getUserLanguages().pipe(
      shareReplay(),
      map(allLocales => allLocales || [])
      // TODO: move dependencies of `LOCALE_ID` to use IUser.userLocaleId
    );
    // user stream
    const user$ = this.userService.getUsers(localUser.id);
    const userPermissions$ = this.adminService.getLocationPermissionForUser(localUser.id);
    const userRecords$ = this.adminService.getRecordPermissionForUser(localUser.id);
    // fork and combine user$ and userLocale$ streams
    return forkJoin([user$, userLocale$, userPermissions$, userRecords$]).pipe(
      map(([user, allLocales, userLocationPermissons, userRecordPerm]) => {
        // get user locale
        const userLocale = this.getUserLocale(user, allLocales);
        // localUser is the user info gotten from the @cbre/sso library
        const userInfo = {
          ...localUser,
          ...user,
          // bind userLocaleId to user
          userLocaleId: userLocale.localeKey,
        };
        this.userInfoResolver.setUserInfo(userInfo);
        if (userLocationPermissons) {
          this.userInfoResolver.setUserLocationPerms(userLocationPermissons);
        }
        // In case the user has no longer acces to a record, it is necessary to update the stored permissions
        this.userInfoResolver.setUserRecordPerm(userRecordPerm ?? []);

        const context = {
          email: userInfo.emailAddress,
          userRole: userInfo.roleVal,
          userGroups: userInfo.groups.map(group => group.value),
          region: userInfo.userCountry,
          lineOfBusiness: userInfo.userLob,
        };
        this.featureToggler.identify(context);

        return user.statusVal === 'Active';
      }),
      map((active: boolean) => {
        if (!active) {
          this.router.navigate(['access-request']);
        }
        return active;
      }),
      catchError(error => {
        this.logger.error({ eventName: 'An error occurred while reviewing user info and languages', error });
        this.router.navigate(['access-request']);
        return of(false);
      })
    );
  }

  canActivateChild(): Observable<boolean> | Promise<boolean> | boolean {
    return this.canActivate();
  }

  private getUserLocale(user: IUser, allLocales: LanguageLocale[]): LanguageLocale {
    const defaultLocale = allLocales.find(locale => locale.defaultLanguage);
    if (defaultLocale) {
      this.appStorageService.setItem<number>(LOCALE_ID, defaultLocale.localeKey);
      return defaultLocale;
    }
    // If the default language is false, the first default language for the user is set and the page is reloaded to continue the normal flow.
    const [locale] = allLocales;
    this.userService
      .setDefaultLang(user.id, locale.localeKey)
      .pipe(untilDestroyed(this))
      .subscribe(() => location.reload());
    return locale;
  }
}
